/**
 * Which Documentation-tab sections a customer should see (Monday 12803784158).
 *
 * Deliberately dependency-free. Every other module under `common/helpers`
 * pulls in `../env`, which reads REACT_APP_COMMON_ENV at import time and
 * throws without it - so anything living there cannot be unit tested. This
 * rule is the part that is easy to get wrong and expensive to get wrong, so it
 * lives on its own and takes everything it needs as arguments.
 *
 * Two decisions from Jaron shape it (Slack, 2026-08-20):
 *   - "if it's a section that is not selected, it should disappear. When it is
 *     selected, it should appear." So sections are hidden, not collapsed.
 *   - "we should probably have ID picture and other documents in a general
 *     section that applies for all." So the old mixed Documentation section
 *     was split in two, and the general half is never gated.
 *
 * The first attempt at this ticket was reverted on Aug 14 because it filtered
 * the LIST without considering the DATA: a customer switched to alarms-only
 * lost all access to Assessment photos already taken - could not view them,
 * re-note them or delete them. `hasMedia` is what prevents that, and it is
 * checked before any product rule for exactly that reason.
 */

/** Product identifiers as stored on the customer, from `useProducts`. */
export const PRODUCT_SECURITY = 'Security'
export const PRODUCT_SOLAR = 'Solar'
export const PRODUCT_HOME_IMPROVEMENTS = 'HIL'
export const PRODUCT_ROOF_CLAIMS = 'RC'

/**
 * @param category A key of `categoryToMediaKinds` — 'Assessment',
 * 'Documentation', 'Solar' or 'Smart Home'.
 * @param currProducts What the customer actually has, e.g. ['Security'].
 * Empty means nothing has been chosen yet.
 * @param hasMedia Whether this category already holds at least one photo or
 * video for this customer.
 * @param isNirvana Whether this is the Nirvana white-label build, which hides
 * Smart Home for a customer with no products rather than showing it.
 */
export const shouldShowCategory = (
  category: string,
  currProducts: readonly string[],
  hasMedia: boolean,
  isNirvana: boolean,
): boolean => {
  // Photos already taken keep their section reachable no matter what the
  // products say. Hiding it would strip the only way to view, re-note or
  // delete them - the defect that caused the Aug 14 revert.
  if (hasMedia) {
    return true
  }

  // Nothing chosen yet means nothing is irrelevant yet. Mirrors the Site
  // Survey accordion's own `currProducts.length === 0` escape, and stops a
  // brand new customer opening onto an almost empty tab.
  const nothingChosenYet = currProducts.length === 0

  if (category === 'Smart Home') {
    return (
      currProducts.includes(PRODUCT_SECURITY) ||
      // Preserves the pre-existing behaviour of `useCategoryToMediaKinds`:
      // a customer with nothing selected sees Smart Home, except on Nirvana.
      (nothingChosenYet && !isNirvana)
    )
  }

  if (category === 'Assessment') {
    return (
      nothingChosenYet ||
      currProducts.includes(PRODUCT_SOLAR) ||
      currProducts.includes(PRODUCT_HOME_IMPROVEMENTS) ||
      // Roof Claims is not in Jaron's wording because it was not on the table:
      // it is absent from his screenshot's product list and its own accordion
      // in Documentation.tsx is commented out, so `roof` under Assessment is
      // the only place those photos can go. Kept as an additive safety - it
      // can only ever show a section, never hide one Jaron asked for.
      currProducts.includes(PRODUCT_ROOF_CLAIMS)
    )
  }

  if (category === 'Solar') {
    return nothingChosenYet || currProducts.includes(PRODUCT_SOLAR)
  }

  // 'Documentation' now holds only the ID picture and other documents, which
  // every job needs, so it is never gated. An unknown category added later
  // also lands here and stays visible - defaulting to hidden would make a new
  // section silently invisible to everyone.
  return true
}
